/**
 * CLE-177 — due-date helpers for the Linear-clone task list.
 *
 * Linear colour-codes due dates by urgency and renders relative labels
 * (Today/Tomorrow/Yesterday) close to now, switching to "Jun 12" further out.
 *
 * The spec suggests chrono-node for natural-language parsing, but that package
 * is not a dependency of this frontend and adding it would bloat the bundle for
 * a single input. Instead we ship a focused parser (`parseNaturalDate` below)
 * covering the documented phrases: "24h", "7 days", "tomorrow", "next monday",
 * "Feb 9", "2026-02-09", etc.
 */

export type DueUrgency = 'overdue' | 'today' | 'tomorrow' | 'soon' | 'future' | 'none';

// Linear's due-date palette (from the issue design tokens).
export const DUE_COLOR: Record<Exclude<DueUrgency, 'none'>, string> = {
  overdue: '#ef4444', // red
  today: '#f97316', // orange
  tomorrow: '#eab308', // yellow
  soon: '#6b7280', // gray (within a week)
  future: '#6b7280', // gray
};

// Tinted background for the urgent buckets (CLE-179 spec). Overdue/Today carry
// a soft fill; Tomorrow and beyond render text-only.
export const DUE_BG: Partial<Record<DueUrgency, string>> = {
  overdue: 'rgba(239,68,68,0.15)',
  today: 'rgba(249,115,22,0.15)',
};

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

/** Whole calendar days from today to the given date (negative = past). */
export function daysUntil(due: Date, now = new Date()): number {
  const ms = startOfDay(due).getTime() - startOfDay(now).getTime();
  return Math.round(ms / 86_400_000);
}

export function dueUrgency(due: string | null, now = new Date()): DueUrgency {
  if (!due) return 'none';
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return 'none';
  const days = daysUntil(d, now);
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days <= 7) return 'soon';
  return 'future';
}

// Parsing keys (English month abbreviations) kept for natural-language input.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Romanian month abbreviations used for display.
const MONTHS_RO = [
  'ian.',
  'feb.',
  'mar.',
  'apr.',
  'mai',
  'iun.',
  'iul.',
  'aug.',
  'sept.',
  'oct.',
  'nov.',
  'dec.',
];

/** Short absolute label: "12 iun." (adds year if not the current one). */
export function formatShortDate(due: string | Date): string {
  const d = typeof due === 'string' ? new Date(due) : due;
  if (Number.isNaN(d.getTime())) return '';
  const base = `${d.getDate()} ${MONTHS_RO[d.getMonth()]}`;
  const thisYear = new Date().getFullYear();
  return d.getFullYear() === thisYear ? base : `${base} ${d.getFullYear()}`;
}

/** Weekday + short date, e.g. "Lun, 15 iun." — used in the popover rows. */
export function formatDayDate(d: Date): string {
  const wd = ['Dum', 'Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm'][d.getDay()];
  return `${wd}, ${formatShortDate(d)}`;
}

/** The row label shown on a task: Astăzi/Mâine/Ieri or "12 iun.". */
export function formatDueLabel(due: string | null, now = new Date()): string {
  if (!due) return '';
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return '';
  const days = daysUntil(d, now);
  if (days === 0) return 'Astăzi';
  if (days === 1) return 'Mâine';
  if (days === -1) return 'Ieri';
  return formatShortDate(d);
}

const WEEKDAYS: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const MONTH_INDEX: Record<string, number> = {};
MONTHS.forEach((m, i) => {
  MONTH_INDEX[m.toLowerCase()] = i;
});
[
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
].forEach((m, i) => {
  MONTH_INDEX[m] = i;
});

// Normalise a "<day> <month>" match into [_, month, day] so both orders share
// the same downstream parsing.
function swapDayMonth(m: RegExpExecArray | null): RegExpExecArray | null {
  if (!m) return null;
  return [m[0], m[2], m[1]] as unknown as RegExpExecArray;
}

/**
 * Parse a natural-language phrase ("24h", "7 days", "next monday", "Feb 9",
 * "2026-02-09") to a Date. Returns null when nothing recognisable is found.
 * Forward-biased: bare dates/weekdays in the past roll to the next occurrence.
 */
export function parseNaturalDate(text: string, now = new Date()): Date | null {
  const raw = text.trim().toLowerCase();
  if (!raw) return null;

  if (raw === 'today') return startOfDay(now);
  if (raw === 'tomorrow') {
    const d = new Date(now);
    d.setDate(now.getDate() + 1);
    return startOfDay(d);
  }
  if (raw === 'yesterday') {
    const d = new Date(now);
    d.setDate(now.getDate() - 1);
    return startOfDay(d);
  }

  // ISO date: 2026-02-09.
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(d.getTime()) ? null : startOfDay(d);
  }

  // Relative offsets: "24h", "48 hours", "7 days", "3d", "2 weeks", "1 month".
  const rel = /^(\d+)\s*(h|hours?|d|days?|w|weeks?|m|months?)$/.exec(raw);
  if (rel) {
    const n = Number(rel[1]);
    const unit = rel[2];
    const d = new Date(now);
    if (unit.startsWith('h')) {
      d.setHours(d.getHours() + n);
      return d;
    }
    if (unit.startsWith('d')) d.setDate(d.getDate() + n);
    else if (unit.startsWith('w')) d.setDate(d.getDate() + n * 7);
    else d.setMonth(d.getMonth() + n);
    return startOfDay(d);
  }

  // Weekday, optionally prefixed with "next".
  const wd = /^(?:next\s+)?(sunday|monday|tuesday|wednesday|thursday|friday|saturday)$/.exec(raw);
  if (wd) {
    const target = WEEKDAYS[wd[1]];
    const d = new Date(now);
    let delta = (target - now.getDay() + 7) % 7;
    if (delta === 0) delta = 7; // upcoming occurrence, not today
    d.setDate(now.getDate() + delta);
    return startOfDay(d);
  }

  // Month + day in either order: "Feb 9", "9 Feb", "February 9".
  const md = /^([a-z]+)\s+(\d{1,2})$/.exec(raw) || swapDayMonth(/^(\d{1,2})\s+([a-z]+)$/.exec(raw));
  if (md) {
    const monthIdx = MONTH_INDEX[md[1]];
    const day = Number(md[2]);
    if (monthIdx !== undefined && day >= 1 && day <= 31) {
      let year = now.getFullYear();
      const candidate = startOfDay(new Date(year, monthIdx, day));
      if (candidate.getTime() < startOfDay(now).getTime()) year += 1;
      return startOfDay(new Date(year, monthIdx, day));
    }
  }

  return null;
}

/** Serialize a Date to the ISO string the API stores (date-only at UTC noon to
 * avoid timezone day-shifts). */
export function toDueISO(d: Date): string {
  const safe = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0));
  return safe.toISOString();
}

export interface QuickDueOption {
  key: string;
  label: string;
  date: Date;
}

/**
 * Today / Tomorrow / End of this week (Fri) / Next Monday / In one week, with
 * computed dates.
 */
export function quickDueOptions(now = new Date()): QuickDueOption[] {
  const today = new Date(now);

  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);

  // End of this week = upcoming Friday (or today if already Friday).
  const endOfWeek = new Date(now);
  const daysToFriday = (5 - now.getDay() + 7) % 7;
  endOfWeek.setDate(now.getDate() + daysToFriday);

  // Next Monday = the Monday after today (a week out when today is Monday).
  const nextMonday = new Date(now);
  nextMonday.setDate(now.getDate() + ((1 - now.getDay() + 7) % 7 || 7));

  const inOneWeek = new Date(now);
  inOneWeek.setDate(now.getDate() + 7);

  return [
    { key: 'today', label: 'Azi', date: today },
    { key: 'tomorrow', label: 'Mâine', date: tomorrow },
    { key: 'eow', label: 'Sfârșitul săptămânii', date: endOfWeek },
    { key: 'nextMonday', label: 'Lunea viitoare', date: nextMonday },
    { key: 'week', label: 'Peste o săptămână', date: inOneWeek },
  ];
}

/** "YYYY-MM-DD" for binding a native <input type="date">. */
export function toDateInputValue(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
